import { useState } from "react";
import type { ReactNode } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";

export interface Crumb {
    label: string;
    onClick?: () => void;
}

interface ActionControlPanelProps {
    onCreate?: () => void;
    /** Extra controls rendered in the left column after "New" (e.g. an Import
        button injected by a consumer that knows the list's model). */
    leftExtra?: ReactNode;
    crumbs: Crumb[];
    title: string;
    center?: ReactNode;
    rightExtra?: ReactNode;
    showPager: boolean;
    total?: number;
    offset: number;
    count: number;
    onPrev: () => void;
    onNext: () => void;
}

// 3-col grid: [+ New + breadcrumb] · [search / selection — always page-centered]
// · [chooser + pager]. View switcher lives in the section menu bar.
export function ActionControlPanel({
    onCreate,
    leftExtra,
    crumbs,
    title,
    center,
    rightExtra,
    showPager,
    total,
    offset,
    count,
    onPrev,
    onNext,
}: ActionControlPanelProps) {
    const start = count > 0 ? offset + 1 : 0;
    const end = offset + count;
    const atStart = offset <= 0;
    const atEnd = total != null ? end >= total : count === 0;

    return (
        <div className="control-panel">
            <div className="cp-left">
                {/* "+ New" and any toolbar extras (e.g. an Import ⋯ wand) share
                    one split-button group so they read as one control. The
                    btn-split hides itself when empty (`:empty` in CSS) and the
                    main button squares its right corner when a wand is present. */}
                <div className="btn-split btn-split--solo">
                    {onCreate ? (
                        <button type="button" className="btn-split__main" onClick={onCreate} data-viewid="ede-action-create">
                            <Plus size={15} />
                            New
                        </button>
                    ) : null}
                    {leftExtra}
                </div>
                <div className="cp-breadcrumb-slot">
                    <div className="breadcrumbs">
                        <BreadcrumbAncestry crumbs={crumbs} />
                        <div className="breadcrumbs__title">{title}</div>
                    </div>
                </div>
            </div>

            {/* Middle grid column — always present so right column stays in col 3. */}
            <div className="cp-center">{center}</div>

            <div className="cp-right">
                {rightExtra}
                {showPager ? (
                    <div className="pager">
                        <span className="pager__count">
                            {start}–{end}
                            {total != null ? ` / ${total}` : ""}
                        </span>
                        <div className="pager__nav">
                            <button className="pager__btn" onClick={onPrev} disabled={atStart} aria-label="Previous page">
                                <ChevronLeft size={15} />
                            </button>
                            <button className="pager__btn" onClick={onNext} disabled={atEnd} aria-label="Next page">
                                <ChevronRight size={15} />
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

// Breadcrumb ancestry — collapses older crumbs into a `…` dropdown so only
// the IMMEDIATE parent stays visible alongside the current title. The user
// still reaches any deeper ancestor in one click via the dropdown. Layout:
//
//   0 crumbs : (nothing — just title)
//   1 crumb  : <crumb> /
//   2+ crumbs: <…dropdown> / <last crumb> /
//
// All ancestors live inside the dropdown when collapse fires, so the menu is
// a stable "jump backwards" affordance no matter how deep the drill chain.
// Exported so FormControlPanel reuses the same DOM/behaviour — one source of
// truth across multi-record and form modes.
export function BreadcrumbAncestry({ crumbs }: { crumbs: Crumb[] }): ReactNode {
    if (crumbs.length === 0) return null;
    if (crumbs.length === 1) {
        const only = crumbs[0];
        return (
            <div className="breadcrumbs__ancestry">
                {only.onClick ? (
                    <button className="breadcrumbs__link" onClick={only.onClick}>{only.label}</button>
                ) : (
                    <span>{only.label}</span>
                )}
            </div>
        );
    }
    const collapsed = crumbs.slice(0, -1);
    const last = crumbs[crumbs.length - 1];
    const [menuOpen, setMenuOpen] = useState(false);
    return (
        <div className="breadcrumbs__ancestry">
            <span className="breadcrumbs__overflow">
                <DropdownMenu.Root open={menuOpen} onOpenChange={setMenuOpen}>
                    <DropdownMenu.Trigger asChild>
                        <button
                            type="button"
                            className={`breadcrumbs__dots${menuOpen ? " is-open" : ""}`}
                            aria-label={`Show ${collapsed.length} earlier breadcrumb${collapsed.length === 1 ? "" : "s"}`}
                        >
                            …
                        </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                        <DropdownMenu.Content
                            className="rdx-menu"
                            align="start"
                            sideOffset={4}
                            // Pointer-driven close shouldn't leave a focus ring on the …
                            // trigger. Radix returns focus on close by default (good for
                            // keyboard), but Chrome treats that programmatic refocus as
                            // focus-visible. Preventing default skips the refocus.
                            onCloseAutoFocus={(e) => e.preventDefault()}
                        >
                            {collapsed.map((c, i) => (
                                <DropdownMenu.Item
                                    key={i}
                                    className="rdx-menu__item"
                                    onSelect={() => c.onClick?.()}
                                    disabled={!c.onClick}
                                >
                                    {c.label}
                                </DropdownMenu.Item>
                            ))}
                        </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                </DropdownMenu.Root>
            </span>
            <span className="breadcrumbs__sep">/</span>
            {last.onClick ? (
                <button className="breadcrumbs__link" onClick={last.onClick}>{last.label}</button>
            ) : (
                <span>{last.label}</span>
            )}
        </div>
    );
}
